import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Countdown from 'react-countdown';
import GreenArrow from '../../../assets/icons/dashboard/greenArrow.svg';
import { ProgramCard } from './ProgramCard';
import { PROGRAM_NAMES } from '../../../helpers/constants';
import { formatNumberWithSpaces } from '../../../helpers/numbers';
import TimerIcon from '../../../assets/icons/passive/timer.svg';
import { Pulse } from '../Pulse';
import { useWeb3React } from '@web3-react/core';
import { usePreviewMode } from '../../../helpers/hooks/useCheckPreviewMode';
import { useOnchainActiveLevels } from '../../../helpers/hooks/useOnchainActiveLevels';

const toSafeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const PassivePrograms = ({ isLoading = false, passiveData, updatePassiveData }) => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const { previewUserAddress } = usePreviewMode();
  const userAddress = (previewUserAddress || account)?.toLowerCase() || null;
  const { x2: onchainX2, x4: onchainX4 } = useOnchainActiveLevels(userAddress);
  const totalProfit = useMemo(() => {
    return (
      toSafeNumber(passiveData?.x2?.passiveData?.totalClaimed) +
      toSafeNumber(passiveData?.x4?.passiveData?.totalClaimed)
    );
  }, [passiveData]);

  const nextClaimTime = useMemo(() => {
    if (!passiveData?.x2?.time || !passiveData?.x4?.time) return null;
    const x2Time = new Date(passiveData.x2.time).getTime();
    const x4Time = new Date(passiveData.x4.time).getTime();
    return Math.min(x2Time, x4Time);
  }, [passiveData]);

  const timerRenderer = ({ days, hours, minutes, seconds }) => {
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return (
      <span className="text-white lg:text-[2rem]">
        {days > 0 ? `${days}d ` : ''}
        {formattedHours}:{formattedMinutes}:{formattedSeconds}
      </span>
    );
  };

  const onComplete = () => {};

  return (
    <div className="flex flex-col w-full space-y-[2.4rem] passive-programs-bg border-[0.1rem] border-solid border-white-50 rounded-[3rem] p-[2rem] backdrop-blur-[1.5rem] lg:border-none lg:p-0">
      <div className="flex items-center justify-between w-full lg:flex-col lg:space-y-[2.4rem] lg:items-start">
        <span className="text-[2.4rem] font-500 tracking-[-0.48px] text-white">{t('passive.programs.title')}</span>
        <div className="flex items-center space-x-[1.2rem] lg:flex-col lg:space-x-0 lg:w-full lg:flex-col-reverse lg:space-y-reverse lg:space-y-[1.2rem]">
          <div className="flex items-center space-x-[1rem] border-[0.1rem] border-dashed border-white-100 pl-[1rem] pr-[1.6rem] text-[1.4rem] font-500 h-[4rem] rounded-[4rem] lg:w-full lg:justify-between lg:pl-[1.6rem] lg:h-[6rem] lg:rounded-[2rem]">
            <div className="flex items-center space-x-[1rem]">
              <img className="lg:w-[2.4rem] lg:h-[2.4rem]" src={TimerIcon} />
              <span className="text-white-500">{t('passive.programs.timeToNextClaim')}</span>
            </div>
            {isLoading ? (
              <Pulse className="h-[2rem] w-[10rem]" />
            ) : nextClaimTime ? (
              <Countdown
                date={new Date(nextClaimTime)}
                renderer={timerRenderer}
                autoStart
                onComplete={onComplete}
                overtime
              />
            ) : (
              <span className="text-white lg:text-[2rem]">--:--:--</span>
            )}
          </div>
          <div className="flex items-center space-x-[1rem] rounded-[4rem] bg-white-40 border-[0.1rem] border-solid border-white-50 h-[4rem] px-[1.6rem] lg:h-[6rem] lg:rounded-[2rem] lg:justify-between lg:w-full">
            <span className="text-[1.4rem] font-500 text-white-500">{t('passive.programs.totalProfit')}</span>
            <div className="flex items-center space-x-[1rem]">
              {isLoading ? (
                <Pulse className="h-[2.4rem] w-[10rem]" />
              ) : (
                <span className="text-[2rem] font-600 text-white">{formatNumberWithSpaces(totalProfit)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-[2rem] lg:flex-col lg:space-x-0 lg:space-y-[2rem]">
        <ProgramCard
          isLoading={isLoading}
          type={PROGRAM_NAMES.X2}
          passiveData={passiveData?.x2}
          updatePassiveData={updatePassiveData}
          maxActiveLevel={onchainX2}
        />
        <ProgramCard
          isLoading={isLoading}
          type={PROGRAM_NAMES.X4}
          passiveData={passiveData?.x4}
          updatePassiveData={updatePassiveData}
          maxActiveLevel={onchainX4}
        />
      </div>
    </div>
  );
};
